import moment from "moment";

import RootStore from "@stores/RootStore";
import { makeAutoObservable, runInAction, when } from "mobx";
import {
  preferences,
  updatePreferences,
  loadFromLocalOrDefault,
  userFeatures,
  userConfigs,
  defaultUserFeatures,
  Features,
  WeightUnit,
  AreaUnit,
  LengthUnit,
  Preferences
} from "@services/demosService";

export enum LANGUAGE {
  RU = "RU",
  EN = "EN",
  ES = "ES",
  UK = "UK",
  PL = "PL",
  DE = "DE",
  FR = "FR",
  RO = "RO",
  HU = "HU",
}
export const LANGUAGE_NAMES = {
  [LANGUAGE.EN]: "English",
  [LANGUAGE.ES]: "Español",
  [LANGUAGE.RU]: "Русский",
  [LANGUAGE.UK]: "Українська",
  [LANGUAGE.PL]: "Polski",
  [LANGUAGE.DE]: "Deutsch",
  [LANGUAGE.FR]: "Français",
  [LANGUAGE.RO]: "Română",
  [LANGUAGE.HU]: "Magyar",
};

export const LANGUAGE_SHORT_NAMES = {
  [LANGUAGE.EN]: "En",
  [LANGUAGE.ES]: "Es",
  [LANGUAGE.RU]: "Ру",
  [LANGUAGE.UK]: "Ук",
  [LANGUAGE.PL]: "Po",
  [LANGUAGE.DE]: "De",
  [LANGUAGE.FR]: "Fr",
  [LANGUAGE.RO]: "Ro",
  [LANGUAGE.HU]: "Ma",
};

export interface ISerializedSettingsStore {
  language: LANGUAGE;
  leftBarExpanded: boolean;
  weightUnit: WeightUnit;
  areaUnit: AreaUnit;
  poundsPerBushel: number;
  poundsPerBag: number;
  bushelCropId: string;
}

export type Units = {
  // Weight measurement unit
  weight: WeightUnit;
  // Area measurement unit
  area: AreaUnit;
  // Length measurement unit. Derived from `area`, not stored — see lengthUnit.
  length: LengthUnit;
  // Number of pounds per one bushel
  poundsPerBushel: number;
  // Number of pounds per one bag
  poundsPerBag: number;
  // Crop the bushel weight was taken from. Display-only: it re-selects the
  // settings picker. Every conversion reads poundsPerBushel, never this.
  // "" means the user typed a weight by hand.
  bushelCropId: string;
};

/** The user-editable settings, as one object so a form can save them in one go. */
export type SettingsPatch = Partial<
  Pick<ISerializedSettingsStore, "language" | "weightUnit" | "areaUnit" | "poundsPerBushel" | "poundsPerBag" | "bushelCropId">
>;

type TouchableKey = keyof ISerializedSettingsStore;

class SettingsStore {
  public readonly rootStore: RootStore;

  get units(): Units {
    return {
      weight: this.weightUnit,
      area: this.areaUnit,
      length: this.lengthUnit,
      poundsPerBushel: this.poundsPerBushel,
      poundsPerBag: this.poundsPerBag,
      bushelCropId: this.bushelCropId,
    };
  }

  language: LANGUAGE = LANGUAGE.EN;
  setLanguage = (lang: LANGUAGE) => {
    moment.locale(lang.toLowerCase());
    this.language = lang;
    this.touch("language");
    this.savePreferences();
  };

  leftBarExpanded: boolean = true;
  setLeftBarExpanded = (v: boolean) => {
    this.leftBarExpanded = v;
    this.touch("leftBarExpanded");
    this.savePreferences();
  }

  weightUnit: WeightUnit = WeightUnit.Ton;
  setWeightUnit = (unit: WeightUnit) => {
    this.weightUnit = unit;
    this.touch("weightUnit");
    this.savePreferences();
  };


  areaUnit: AreaUnit = AreaUnit.Hectare;
  setAreaUnit = (unit: AreaUnit) => {
    this.areaUnit = unit;
    this.touch("areaUnit");
    this.savePreferences();
  };

  // Lengths (header/reaper widths) follow the area unit rather than getting
  // their own control: a customer on acres thinks in feet. Derived, so there is
  // nothing extra to persist or migrate.
  get lengthUnit(): LengthUnit {
    return this.areaUnit === AreaUnit.Acre ? LengthUnit.Foot : LengthUnit.Meter;
  }

  poundsPerBushel: number = 60;
  setPoundsPerBushel = (pounds: number) => {
    this.poundsPerBushel = pounds;
    this.touch("poundsPerBushel");
    this.savePreferences();
  };

  // Which crop's standard bushel weight poundsPerBushel came from, so the
  // settings picker reopens on it. "" = typed by hand. Never read by a
  // conversion — poundsPerBushel stays the single source of truth.
  bushelCropId: string = "";
  setBushelCropId = (cropId: string) => {
    this.bushelCropId = cropId;
    this.touch("bushelCropId");
    this.savePreferences();
  };

  poundsPerBag: number = 100;
  setPoundsPerBag = (pounds: number) => {
    this.poundsPerBag = pounds;
    this.touch("poundsPerBag");
    this.savePreferences();
  };

  /**
   * Apply several settings with ONE save. The settings form used to call five
   * setters in a row, which was five writes (now five PUTs) for one click.
   */
  updateSettings = (patch: SettingsPatch) => {
    if (patch.language !== undefined) {
      moment.locale(patch.language.toLowerCase());
      this.language = patch.language;
      this.touch("language");
    }
    if (patch.weightUnit !== undefined) {
      this.weightUnit = patch.weightUnit;
      this.touch("weightUnit");
    }
    if (patch.areaUnit !== undefined) {
      this.areaUnit = patch.areaUnit;
      this.touch("areaUnit");
    }
    if (patch.poundsPerBushel !== undefined) {
      this.poundsPerBushel = patch.poundsPerBushel;
      this.touch("poundsPerBushel");
    }
    if (patch.poundsPerBag !== undefined) {
      this.poundsPerBag = patch.poundsPerBag;
      this.touch("poundsPerBag");
    }
    if (patch.bushelCropId !== undefined) {
      this.bushelCropId = patch.bushelCropId;
      this.touch("bushelCropId");
    }
    this.savePreferences();
  };

  /**
   * True once the signed-in user's stored preferences have been applied (or
   * definitively failed to load). Until then the store holds the pre-auth
   * values — whatever this browser saved last, else defaults — and a form that
   * seeds itself from the store at mount must re-seed when this flips. That
   * missing re-seed is why "changed hectares to acres, saved, reloaded, saw
   * hectares again" happened: the store had acres, the form did not.
   */
  prefsLoaded = false;

  /**
   * Settings the user changed in THIS page load before the stored preferences
   * arrived — e.g. picking a language on the login page, or clicking a unit in
   * the first half-second after sign-in. Those choices win over the stored
   * copy field by field; everything else comes from storage.
   */
  private touched = new Set<TouchableKey>();
  private touch = (key: TouchableKey) => {
    this.touched.add(key);
  };

  features: Features;
  private setFeatures = (features: Features) => {
    this.features = features;
  }

  // Admin-managed feature-flag keys enabled for the current user. Gate a
  // feature under test with settingsStore.hasConfig("some_key").
  enabledConfigs: string[] = [];
  private setEnabledConfigs = (keys: string[]) => {
    this.enabledConfigs = keys;
  };
  hasConfig = (key: string): boolean => this.enabledConfigs.includes(key);

  constructor(rootStore: RootStore) {
    makeAutoObservable<SettingsStore, "touched">(this, { touched: false });
    this.rootStore = rootStore;
    // Features
    this.features = defaultUserFeatures();

    // Before sign-in: the last preferences saved in this browser (by whoever
    // used it last), so the login page comes up in the language chosen last
    // time instead of English. Nothing sensitive lives here — language, units
    // and the sidebar state.
    this.loadFrom(loadFromLocalOrDefault());

    when(() => this.rootStore.accountStore.authenticated, () => {
      userConfigs()
        .then((keys) => this.setEnabledConfigs(keys))
        .catch((e) => console.error("Failed to get user configs", e));
      userFeatures()
        .then((features) => this.setFeatures(features))
        .catch((e) => console.error("Failed to get user features", e))
        .finally(() => this.loadStoredPreferences());
    });
  }

  // The real logged-in user (never the impersonated owner). Preferences are
  // scoped to this identity so a team member keeps their own units across every
  // account they view, and never alters the owner's units.
  private get prefsUserId(): string | undefined {
    return this.rootStore.accountStore.account?.id;
  }

  /**
   * Load the signed-in user's preferences: server copy first, then this
   * browser's copy, then defaults. Anything the user already touched in this
   * page load is kept. Whatever the outcome, the result is written back so
   * the server has a copy from now on (this is how pre-2026-09 users, whose
   * preferences only ever lived in localStorage, get migrated).
   */
  private loadStoredPreferences = async () => {
    const userId = this.prefsUserId;
    try {
      const { prefs, source } = await preferences(userId);
      runInAction(() => {
        this.applyKeepingTouched(prefs);
        if (source !== "server" || this.touched.size > 0) this.savePreferences();
      });
    } catch (e) {
      console.warn("Failed to load preferences from server, falling back to local/default", e);
      runInAction(() => this.applyKeepingTouched(loadFromLocalOrDefault(userId)));
    } finally {
      runInAction(() => {
        this.prefsLoaded = true;
      });
    }
  };

  private applyKeepingTouched(prefs: Preferences) {
    const keep = this.serialize();
    this.loadFrom(prefs);
    for (const key of this.touched) {
      // Same field set on both sides; the cast is only because TS cannot
      // relate the key to its value type across the two objects.
      (this as unknown as Record<TouchableKey, unknown>)[key] = keep[key];
    }
    moment.locale(this.language.toLowerCase());
  }

  private savePreferences() {
    updatePreferences(
      {
        localization: { language: this.language },
        ui: { left_bar_expanded: this.leftBarExpanded },
        units: {
          weight: this.weightUnit,
          area: this.areaUnit,
          poundsPerBushel: this.poundsPerBushel,
          poundsPerBag: this.poundsPerBag,
          bushelCropId: this.bushelCropId,
        },
        updated_at: Date.now(),
      },
      this.prefsUserId
    ).catch((e) => {
      console.error("Failed to save preferences", e);
    });
  }

  private loadFrom(prefs: Preferences) {
    this.language = prefs.localization.language;
    // Every path that sets the language also sets moment's, so dates never
    // render in whatever locale moment was last left in.
    moment.locale(this.language.toLowerCase());
    this.leftBarExpanded = prefs.ui.left_bar_expanded;
    this.weightUnit = prefs.units.weight;
    this.areaUnit = prefs.units.area;
    this.poundsPerBushel = prefs.units.poundsPerBushel;
    this.poundsPerBag = prefs.units.poundsPerBag;
    // Absent on rows written before the picker existed; "" just means the
    // picker opens on Custom, showing the weight already stored.
    this.bushelCropId = prefs.units.bushelCropId ?? "";
  }

  serialize = (): ISerializedSettingsStore => ({
    language: this.language,
    leftBarExpanded: this.leftBarExpanded,
    weightUnit: this.weightUnit,
    areaUnit: this.areaUnit,
    poundsPerBushel: this.poundsPerBushel,
    poundsPerBag: this.poundsPerBag,
    bushelCropId: this.bushelCropId,
  });
}

export default SettingsStore;
